import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtGHS, fmtNum, fmtDate, fmtDateTime, daysUntil, pName } from "@/lib/format";
import { Search, PackagePlus, SlidersHorizontal, AlertTriangle, Upload } from "lucide-react";
import { OpeningStockDialog } from "@/components/OpeningStockDialog";
import { StockEditDialog } from "@/components/StockEditDialog";
import { ScanBox } from "@/components/ScanBox";
import { ProductCombobox, ScanButton, scanOnEnter } from "@/components/ProductCombobox";
import { PackEntry } from "@/components/PackEntry";
import { toast } from "sonner";

export default function Inventory() {
  const { can, isSuperAdmin } = useAuth();
  const [editRow, setEditRow] = useState(null);
  const { branchParam, branches, activeBranch, changeBranch } = useBranch();
  const [tab, setTab] = useState("stock");
  const [stock, setStock] = useState([]);
  const [batches, setBatches] = useState([]);
  const [moves, setMoves] = useState([]);
  const [alerts, setAlerts] = useState({ near_expiry: [], expired: [], low_stock: [] });
  const [q, setQ] = useState("");

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [recv, setRecv] = useState({ product_id: "", branch_id: "", batch_no: "", expiry_date: "", quantity: 0, cost_price: 0, received_ref: "" });
  const [adjust, setAdjust] = useState(null);

  const [osOpen, setOsOpen] = useState(false);
  const loadAll = useCallback(() => {
    api.get("/stock", { params: { branch_id: branchParam, q: q || undefined } }).then((r) => setStock(r.data));
    api.get("/batches", { params: { branch_id: branchParam } }).then((r) => setBatches(r.data));
    api.get("/stock/movements", { params: { branch_id: branchParam } }).then((r) => setMoves(r.data));
    api.get("/stock/alerts", { params: { branch_id: branchParam } }).then((r) => setAlerts(r.data));
  }, [branchParam, q]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  const handleReceiveScan = async (code) => {
    let p = null;
    try { const { data } = await api.get("/products", { params: { barcode: code } }); p = (data || []).find((x) => x.is_active !== false); } catch { /* ignore */ }
    if (!p) { toast.error(`No product matches barcode ${code}`); return; }
    setRecv((r) => ({ ...r, product_id: p.id }));
    toast.success(`${pName(p)} selected`);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="receive-batch-input"]');
      if (el) el.focus();
    }, 60);
  };

  const recvReady = recv.product_id && recv.branch_id && recv.batch_no.trim() && recv.expiry_date && Number(recv.quantity) > 0 && Number(recv.cost_price) > 0;
  const submitReceive = async () => {
    try {
      await api.post("/batches", { ...recv, quantity: Number(recv.quantity), cost_price: Number(recv.cost_price) });
      toast.success("Stock received");
      setReceiveOpen(false); setRecv({ product_id: "", branch_id: "", batch_no: "", expiry_date: "", quantity: 0, cost_price: 0, received_ref: "" });
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitAdjust = async () => {
    try {
      await api.post("/stock/adjust", { batch_id: adjust.id, new_quantity: Number(adjust.new_quantity), reason: adjust.reason });
      toast.success("Stock adjusted");
      setAdjust(null); loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const branchFilter = (
    <Select value={activeBranch} onValueChange={changeBranch}>
      <SelectTrigger data-testid="inventory-branch-filter" className="w-full sm:w-56"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All branches</SelectItem>
        {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <PageHeader
        title="Inventory"
        subtitle="Batch-level stock, expiry tracking and the full movement ledger"
        actions={can("inventory.receive") && (<>
          <Button variant="outline" data-testid="opening-stock-btn" onClick={() => setOsOpen(true)}>
            <Upload className="h-4 w-4 mr-1.5" /> Upload opening stock
          </Button>
          <Button data-testid="receive-stock-btn" onClick={() => { setRecv((r) => ({ ...r, branch_id: branchParam || branches[0]?.id || "" })); setReceiveOpen(true); }}>
            <PackagePlus className="h-4 w-4 mr-1.5" /> Receive Stock
          </Button>
        </>)}
      />
      <OpeningStockDialog open={osOpen} onOpenChange={setOsOpen} branches={branches} onDone={loadAll} />
      <StockEditDialog row={editRow} onClose={() => setEditRow(null)} onDone={loadAll} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock on Hand</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches">Batches</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            Alerts {(alerts.expired.length + alerts.near_expiry.length) > 0 && (
              <span className="ml-1.5 text-xs bg-rose-100 text-rose-600 rounded-full px-1.5">{alerts.expired.length + alerts.near_expiry.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Movement Ledger</TabsTrigger>
        </TabsList>

        {/* Stock */}
        <TabsContent value="stock" className="mt-4">
          <Card className="p-3 mb-4 shadow-sm flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
              <Input data-testid="stock-search-input" placeholder="Search name, code or barcode…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={scanOnEnter(products, (p) => setQ(p.sku))} className="pl-9" />
            </div>
            <ScanButton products={products} onProduct={(p) => setQ(p.sku)} testId="stock-scan-btn" />
            {branchFilter}
          </Card>
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">SKU</th><th className="py-2.5 px-3">Medicine</th><th className="py-2.5 px-3">Branch</th>
                <th className="py-2.5 px-3 text-right">On Hand</th><th className="py-2.5 px-3 text-right">Batches</th>
                <th className="py-2.5 px-3 text-right">Reorder</th><th className="py-2.5 px-3">Status</th></tr>
              </thead>
              <tbody>
                {stock.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">No stock</td></tr>}
                {stock.map((s, i) => (
                  <tr key={i} className={`border-t border-border hover:bg-muted/50 ${isSuperAdmin ? "cursor-pointer" : ""}`} data-testid={`stock-row-${s.sku}-${s.branch_id}`}
                    onClick={isSuperAdmin ? () => setEditRow(s) : undefined} title={isSuperAdmin ? "Click to edit quantities" : undefined}>
                    <td className="py-2 px-3 font-mono text-xs">{s.sku}</td>
                    <td className="py-2 px-3"><div className="font-medium text-foreground">{s.generic_name}{s.unit_of_measure && <span className="ml-1.5 text-xs font-normal text-muted-foreground">per {s.unit_of_measure}</span>}</div></td>
                    <td className="py-2 px-3 text-muted-foreground">{s.branch_name}</td>
                    <td className="py-2 px-3 text-right font-mono font-semibold">{fmtNum(s.total_qty)}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{s.batch_count}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{s.reorder_level}</td>
                    <td className="py-2 px-3"><StatusBadge value={s.status} label={s.status.toUpperCase()} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* Batches */}
        <TabsContent value="batches" className="mt-4">
          <Card className="p-3 mb-4 shadow-sm flex justify-end">{branchFilter}</Card>
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">Batch No.</th><th className="py-2.5 px-3">Medicine</th><th className="py-2.5 px-3">Branch</th>
                <th className="py-2.5 px-3">Expiry</th><th className="py-2.5 px-3 text-right">Qty</th><th className="py-2.5 px-3 text-right">Cost</th><th className="py-2.5 px-3"></th></tr>
              </thead>
              <tbody>
                {batches.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">No batches</td></tr>}
                {batches.map((b) => {
                  const d = daysUntil(b.expiry_date);
                  const expTone = d < 0 ? "text-rose-600 font-semibold" : d <= 90 ? "text-amber-600 font-medium" : "text-muted-foreground";
                  return (
                    <tr key={b.id} className="border-t border-border hover:bg-muted/50">
                      <td className="py-2 px-3 font-mono text-xs">{b.batch_no}</td>
                      <td className="py-2 px-3"><div className="font-medium text-foreground">{b.generic_name}</div><div className="text-xs text-muted-foreground">{b.sku}</div></td>
                      <td className="py-2 px-3 text-muted-foreground">{b.branch_name}</td>
                      <td className={`py-2 px-3 font-mono text-xs ${expTone}`}>{fmtDate(b.expiry_date)}{d < 0 ? " (expired)" : d <= 90 ? ` (${d}d)` : ""}</td>
                      <td className="py-2 px-3 text-right font-mono font-semibold">{fmtNum(b.quantity_on_hand)}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs">{fmtGHS(b.cost_price)}</td>
                      <td className="py-2 px-3 text-right">
                        {can("inventory.adjust") && (
                          <Button variant="ghost" size="sm" data-testid={`adjust-batch-${b.batch_no}`} onClick={() => setAdjust({ ...b, new_quantity: b.quantity_on_hand, reason: "" })}>
                            <SlidersHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* Alerts */}
        <TabsContent value="alerts" className="mt-4 space-y-6">
          <AlertBlock title="Expired Stock" tone="rose" rows={alerts.expired} branchName={branchName} />
          <AlertBlock title={`Near Expiry (within ${alerts.near_expiry_days || 90} days)`} tone="amber" rows={alerts.near_expiry} branchName={branchName} />
          <Card className="p-4 shadow-sm">
            <h3 className="font-display font-semibold text-foreground mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Low / Out of Stock</h3>
            {alerts.low_stock.length === 0 ? <div className="text-sm text-muted-foreground">All good</div> : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground text-left border-b border-border"><tr><th className="py-2">Medicine</th><th className="py-2">Branch</th><th className="py-2 text-right">On Hand</th><th className="py-2 text-right">Reorder</th><th className="py-2">Status</th></tr></thead>
                <tbody>{alerts.low_stock.map((s, i) => (<tr key={i} className="border-b border-border"><td className="py-2">{s.generic_name}</td><td className="py-2 text-muted-foreground">{s.branch_name}</td><td className="py-2 text-right font-mono">{s.total_qty}</td><td className="py-2 text-right font-mono">{s.reorder_level}</td><td className="py-2"><StatusBadge value={s.status} label={s.status.toUpperCase()} /></td></tr>))}</tbody>
              </table></div>
            )}
          </Card>
        </TabsContent>

        {/* Ledger */}
        <TabsContent value="ledger" className="mt-4">
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">When</th><th className="py-2.5 px-3">Type</th><th className="py-2.5 px-3">Medicine</th>
                <th className="py-2.5 px-3 text-right">Qty</th><th className="py-2.5 px-3">Ref</th><th className="py-2.5 px-3">By</th></tr>
              </thead>
              <tbody>
                {moves.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No movements</td></tr>}
                {moves.map((m) => (
                  <tr key={m.id} className="border-t border-border hover:bg-muted/50">
                    <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(m.timestamp)}</td>
                    <td className="py-2 px-3"><span className="text-xs font-medium uppercase text-muted-foreground">{m.type.replace(/_/g, " ")}</span></td>
                    <td className="py-2 px-3">{m.generic_name}</td>
                    <td className={`py-2 px-3 text-right font-mono font-semibold ${m.quantity < 0 ? "text-rose-600" : "text-emerald-600"}`}>{m.quantity > 0 ? "+" : ""}{m.quantity}</td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{m.ref_type}{m.note ? ` · ${m.note}` : ""}</td>
                    <td className="py-2 px-3 text-muted-foreground text-xs">{m.actor_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Receive dialog */}
      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Receive Stock (GRN)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <ScanBox onScan={handleReceiveScan} placeholder="Scan a pack to select product…" />
            <div><Label>Product</Label>
              <ProductCombobox products={products} value={recv.product_id} onChange={(v) => setRecv((r) => ({ ...r, product_id: v }))} testId="receive-product-select" />
            </div>
            <div><Label>Branch / Location</Label>
              <Select value={recv.branch_id} onValueChange={(v) => setRecv((r) => ({ ...r, branch_id: v }))}>
                <SelectTrigger data-testid="receive-branch-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Batch No. <span className="text-rose-600">*</span></Label><Input data-testid="receive-batch-input" value={recv.batch_no} onChange={(e) => setRecv((r) => ({ ...r, batch_no: e.target.value }))} /></div>
              <div><Label>Expiry date <span className="text-rose-600">*</span></Label><Input type="date" data-testid="receive-expiry-input" value={recv.expiry_date} onChange={(e) => setRecv((r) => ({ ...r, expiry_date: e.target.value }))} /></div>
              <div><Label>Quantity <span className="text-rose-600">*</span></Label><Input type="number" data-testid="receive-qty-input" value={recv.quantity} onChange={(e) => setRecv((r) => ({ ...r, quantity: e.target.value }))} /></div>
              <div><Label>Unit cost (GHS) <span className="text-rose-600">*</span></Label><Input type="number" step="0.01" min={0} data-testid="receive-cost-input" value={recv.cost_price} onChange={(e) => setRecv((r) => ({ ...r, cost_price: e.target.value }))} /></div>
            </div>
            <PackEntry product={products.find((p) => p.id === recv.product_id)} testId="receive-pack" quantity={recv.quantity} unitCost={recv.cost_price}
              onApply={({ quantity, unitCost }) => setRecv((r) => ({ ...r, ...(quantity !== undefined && { quantity }), ...(unitCost !== undefined && { cost_price: unitCost }) }))} />
            <div><Label>GRN / Reference</Label><Input value={recv.received_ref} onChange={(e) => setRecv((r) => ({ ...r, received_ref: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveOpen(false)}>Cancel</Button>
            <Button data-testid="submit-receive-btn" onClick={submitReceive} disabled={!recvReady}>Receive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust dialog */}
      <Dialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust Stock — {adjust?.batch_no}</DialogTitle></DialogHeader>
          {adjust && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{adjust.generic_name} · current {adjust.quantity_on_hand}</div>
              <div><Label>New quantity</Label><Input type="number" data-testid="adjust-qty-input" value={adjust.new_quantity} onChange={(e) => setAdjust((a) => ({ ...a, new_quantity: e.target.value }))} /></div>
              <div><Label>Reason</Label><Input data-testid="adjust-reason-input" placeholder="e.g. damage, count correction, expiry write-off" value={adjust.reason} onChange={(e) => setAdjust((a) => ({ ...a, reason: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjust(null)}>Cancel</Button>
            <Button data-testid="submit-adjust-btn" onClick={submitAdjust} disabled={!adjust?.reason}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AlertBlock({ title, tone, rows, branchName }) {
  const border = tone === "rose" ? "border-rose-200" : "border-amber-200";
  return (
    <Card className={`p-4 shadow-sm border ${border}`}>
      <h3 className="font-display font-semibold text-foreground mb-3">{title} <span className="text-muted-foreground font-normal">({rows.length})</span></h3>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground">None</div> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground text-left border-b border-border"><tr><th className="py-2">Batch</th><th className="py-2">Medicine</th><th className="py-2">Branch</th><th className="py-2">Expiry</th><th className="py-2 text-right">Qty</th></tr></thead>
          <tbody>{rows.map((b) => (<tr key={b.id} className="border-b border-border"><td className="py-2 font-mono text-xs">{b.batch_no}</td><td className="py-2">{b.generic_name}</td><td className="py-2 text-muted-foreground">{b.branch_name}</td><td className="py-2 font-mono text-xs">{fmtDate(b.expiry_date)}</td><td className="py-2 text-right font-mono">{b.quantity_on_hand}</td></tr>))}</tbody>
        </table></div>
      )}
    </Card>
  );
}
